import { z } from 'zod';
import {
  REPLAY_MODES,
  REPLAY_TRIGGERS,
  type ReplayMode,
  type ReplayTrigger,
} from './constants.js';

/**
 * §22 — Session replay sözleşmesi.
 *
 * SDK ile ingest arasındaki TEK anlaşma budur. `apps/tracker-sdk` sıfır runtime
 * bağımlılığı taşıdığı için replay bundle'ı buradan YALNIZCA `import type` alır;
 * ingest zod şemasını runtime doğrulama için kullanır.
 */

/* -------------------------------------------------------------------------- */
/* Olaylar (§22.4 tablosu)                                                     */
/* -------------------------------------------------------------------------- */

/** §22.4 — serileştirilmiş DOM düğümü. Metin düğümü `{text}` taşır (maskeli). */
export interface ReplayNode {
  /** öğe etiketi (küçük harf); metin düğümünde yok */
  tag?: string;
  /** §22.4 whitelist'ten geçmiş attribute'lar */
  attrs?: Record<string, string>;
  children?: ReplayNode[];
  /** metin düğümü içeriği — §22.4 gereği varsayılan maskeli */
  text?: string;
  /** oynatıcının mutation hedefini bulması için kararlı düğüm kimliği */
  n?: number;
  /**
   * §36.4 — AÇIK (open) shadow root'un içeriği.
   *
   * Kökün kendi `n`'i AYRI taşınır: shadow içindeki childList mutation'ının hedefi
   * KÖKtür, host öğe değil; kimliksiz kök gelen mutation'ları oynatıcı bağlayamaz.
   *
   * `closed` root'lar için alan YOKTUR: `Element.shadowRoot` closed modda `null` döner
   * ve DOM API'si varlığını bildiren BAŞKA bir yol sunmaz — "kapalı shadow var" bilgisi
   * ölçülemez, yer tutucu üretmek uydurma olurdu (§36.4 düzeltmesi).
   */
  shadow?: { n: number; children: ReplayNode[] };
  /** §36.3 — SAME-ORIGIN iframe'in belge kökü (maskeleme aynen uygulanmıştır). */
  frame?: ReplayNode;
  /**
   * §36.3/§36.2 — içeriği OKUNAMAYAN öğe için yer tutucu.
   * `'x'` = cross-origin iframe, `'c'` = anlık görüntüsü alınamayan canvas.
   */
  ph?: 'x' | 'c';
  /** §36.2 — canvas anlık görüntüsü (`data:` URI). */
  img?: string;
}

export type ReplayEventType =
  | 'net'
  | 'canvas'
  | 'snapshot'
  | 'mutation'
  | 'input'
  | 'scroll'
  | 'mouse'
  | 'click'
  | 'resize'
  | 'page';

/** §22.4 — her olay `{t, ts, ...}`; `ts` chunk başlangıcına göre rölatif ms. */
export interface ReplayEvent {
  t: ReplayEventType;
  ts: number;
  [key: string]: unknown;
}

/* -------------------------------------------------------------------------- */
/* Chunk zarfı (§22.5)                                                         */
/* -------------------------------------------------------------------------- */

export interface ReplayChunk {
  v: 1;
  pid: string;
  sid: string;
  /** oturum içi 0'dan artan chunk sırası */
  seq: number;
  mode: ReplayMode;
  trigger: ReplayTrigger;
  /** chunk zaman tabanı — epoch ms */
  startedAt: number;
  /** §22.3 — oturum tavanı aşıldıysa son chunk'ta 1 */
  truncated?: 0 | 1;
  events: ReplayEvent[];
}

const nodeSchema: z.ZodType<ReplayNode> = z.lazy(() =>
  z
    .object({
      tag: z.string().max(64).optional(),
      attrs: z.record(z.string()).optional(),
      children: z.array(nodeSchema).optional(),
      text: z.string().optional(),
      n: z.number().int().nonnegative().optional(),
    })
    .passthrough(),
);

export const replayEventSchema = z
  .object({
    /*
     * §35 — 'net' Faz 3'te eklendi. Props şemaları `.passthrough()` olduğu için yalnız
     * bu enum'a bir değer eklemek YETER; migration gerekmez.
     */
    t: z.enum([
      'snapshot',
      'mutation',
      'input',
      'scroll',
      'mouse',
      'click',
      'resize',
      'page',
      'net',
      'canvas',
    ]),
    ts: z.number().nonnegative(),
  })
  .passthrough();

export const replayChunkSchema = z.object({
  v: z.literal(1),
  pid: z.string().min(1).max(64),
  sid: z.string().min(1).max(128),
  seq: z.number().int().nonnegative().max(65535),
  mode: z.enum(REPLAY_MODES),
  trigger: z.enum(REPLAY_TRIGGERS),
  startedAt: z.number().int().finite(),
  truncated: z.union([z.literal(0), z.literal(1)]).optional(),
  events: z.array(replayEventSchema).max(20000),
});

export interface ReplayValidateResult {
  chunk: ReplayChunk | null;
  reason?: string;
}

/**
 * §22.5 — ingest doğrulaması. Geçersiz chunk SESSİZCE atılır (204); replay
 * kayıp-toleranslıdır, hata istemciye bilgi sızdırmaz.
 */
export function validateReplayChunk(raw: unknown): ReplayValidateResult {
  /*
   * ASLA FIRLATMAZ — bu bir SÖZLEŞME garantisidir, gözlenmiş bir hatanın yaması değil.
   * Gövde kimliksiz bir POST'tan gelir ve `nodeSchema` özyinelemelidir (derinlik
   * sınırı YOK). Mevcut zod sürümü 40.000 seviyede fırlatmıyor (ölçüldü), ama
   * fırlatan bir sürüm/şema değişikliği rotayı 500'e düşürürdü; oysa §22.5
   * geçersiz chunk'ın SESSİZCE 204 ile atılmasını şart koşar. Maliyeti sıfır
   * olan bu sarmalayıcı sözleşmeyi zod'un davranışından bağımsız kılar.
   */
  try {
    const parsed = replayChunkSchema.safeParse(raw);
    if (!parsed.success) {
      const first = parsed.error.issues[0];
      return {
        chunk: null,
        reason: first ? `${first.path.join('.') || '<root>'}: ${first.message}` : 'geçersiz chunk',
      };
    }
    return { chunk: parsed.data as ReplayChunk };
  } catch (err) {
    return { chunk: null, reason: `doğrulama başarısız: ${(err as Error).name}` };
  }
}

/* -------------------------------------------------------------------------- */
/* Çekirdek ↔ replay köprüsü (§22.2)                                           */
/* -------------------------------------------------------------------------- */

export interface ReplayStartConfig {
  mode: ReplayMode;
  /** §22.3 — oturum bayt tavanı; profil tablosundan gelir */
  sessionMaxBytes: number;
}

/** §22.2 — `window.__dxpReplay`; replay.js yüklenince kendini kaydeder. */
export interface ReplayRecorder {
  start(cfg: ReplayStartConfig): void;
  trigger(reason: Exclude<ReplayTrigger, 'sampled'>): void;
  /**
   * §35.2 — ağ isteği meta verisi. GÖVDE, BAŞLIK ve QUERY DEĞERLERİ ASLA GEÇMEZ;
   * çağıran yalnız maskelenmiş path'i ve sayısal alanları verir (§35.1 sınırı).
   * `res` = yanıt bayt sayısı (`content-length` varsa); yoksa alan hiç yazılmaz.
   */
  net?(method: string, path: string, status: number, ms: number, res?: number): void;
  /** tavan aşımı veya consent(false) */
  stop(): void;
}

/** §8.1 — ClickHouse `dxp.replay_chunks` satırı (004_replay.sql). */
export interface ReplayChunkRow {
  pid: string;
  sid: string;
  seq: number;
  mode: ReplayMode;
  trigger: ReplayTrigger;
  /** epoch ms */
  started_at: number;
  body: string;
  bytes: number;
  truncated: 0 | 1;
}

/** §22.7 — panel chunk listesi (gövdesiz). */
export interface ReplayChunkMeta {
  seq: number;
  mode: ReplayMode;
  trigger: ReplayTrigger;
  bytes: number;
  started_at: number;
  /**
   * §33.4 — oturum bayt tavanı aşıldığı için kayıt BURADA KESİLDİ (§22.3).
   *
   * Kolon (`dxp.replay_chunks.truncated`) MVP'den beri VARDI ama hiçbir uç onu
   * döndürmüyordu: panelde kaydı izleyen kişi kaydın bütçe nedeniyle kesildiğini
   * göremiyor, oynatıcının sessizce erken bitmesini "replay bozuk" diye okuyordu.
   */
  truncated: 0 | 1;
}

export interface ReplayListResponse {
  chunks: ReplayChunkMeta[];
  totalBytes: number;
}
